import React, { useState, useRef, useCallback, useEffect } from 'react'
import axios from 'axios'
import { openAIUrl } from 'urls'
import { getAxiosDefaultConfig } from 'utils'
import VoiceAnimation from '../VoiceInput/VoiceAnimation'

interface VoiceOutputProps {
  text: string | null
  autoPlay?: boolean
  voice?: 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer'
  maxLength?: number
  onPlayStart?: () => void
  onPlayEnd?: () => void
  onError?: (error: string) => void
  className?: string
  showAnimation?: boolean
}

// Placeholder for decimal points during sentence detection
const DECIMAL_PLACEHOLDER = '\u2024' // One dot leader - visually similar but distinct

/**
 * Protects decimal numbers from being split as sentence boundaries
 * e.g., "23.6 million" -> "23\u20246 million"
 */
const protectDecimals = (text: string): string => {
  return text.replace(/(\d)\.(\d)/g, `$1${DECIMAL_PLACEHOLDER}$2`)
}

/**
 * Restores decimal points after sentence detection
 */
const restoreDecimals = (text: string): string => {
  return text.replace(new RegExp(DECIMAL_PLACEHOLDER, 'g'), '.')
}

/**
 * Extracts a short summary from the text for TTS
 * Takes the first 1-2 sentences, up to maxLength characters
 */
const extractSummary = (text: string, maxLength: number): string => {
  if (!text) return ''

  // Remove markdown formatting
  let cleaned = text
    .replace(/#{1,6}\s*/g, '') // Remove headers
    .replace(/\*\*|__/g, '') // Remove bold
    .replace(/\*|_/g, '') // Remove italic
    .replace(/`{1,3}[^`]*`{1,3}/g, '') // Remove code blocks
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert links to just text
    .replace(/!\[[^\]]*\]\([^)]+\)/g, '') // Remove images
    .replace(/^\s*[-*+]\s+/gm, '') // Remove list markers
    .replace(/^\s*\d+\.\s+/gm, '') // Remove numbered list markers
    .replace(/\n+/g, ' ') // Replace newlines with spaces
    .trim()

  // Protect decimal numbers before sentence detection (e.g., "23.6" -> "23⁄6")
  const protected_ = protectDecimals(cleaned)

  // Find sentence boundaries
  const sentencesRaw = protected_.match(/[^.!?]+[.!?]+/g) || [protected_]

  // Restore decimal points in each sentence
  const sentences = sentencesRaw.map(restoreDecimals)

  let summary = ''
  for (const sentence of sentences) {
    if (summary.length + sentence.length <= maxLength) {
      summary += sentence
    } else {
      break
    }
    // Stop after 2 sentences
    if (summary.match(/[.!?]/g)?.length >= 2) break
  }

  // If no sentences found, truncate at word boundary
  if (!summary && cleaned.length > 0) {
    summary = cleaned.slice(0, maxLength)
    const lastSpace = summary.lastIndexOf(' ')
    if (lastSpace > maxLength * 0.7) {
      summary = summary.slice(0, lastSpace)
    }
    summary += '...'
  }

  return summary.trim()
}

type SpeakOptions = {
  /** Bypass dedup so manual replay of the same message still works. */
  force?: boolean
}

const VoiceOutput: React.FC<VoiceOutputProps> = ({
  text,
  autoPlay = true,
  voice = 'nova',
  maxLength = 300,
  onPlayStart,
  onPlayEnd,
  onError,
  className = '',
  showAnimation = true,
}) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const audioUrlRef = useRef<string | null>(null)
  const isLoadingRef = useRef(false)
  const lastAutoTextRef = useRef<string | null>(null)
  const lastSpokenSummaryRef = useRef<string | null>(null)
  const onPlayStartRef = useRef(onPlayStart)
  const onPlayEndRef = useRef(onPlayEnd)
  const onErrorRef = useRef(onError)

  useEffect(() => {
    onPlayStartRef.current = onPlayStart
    onPlayEndRef.current = onPlayEnd
    onErrorRef.current = onError
  }, [onPlayStart, onPlayEnd, onError])

  const releaseActiveAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.onplay = null
      audioRef.current.onended = null
      audioRef.current.onerror = null
      audioRef.current.pause()
      audioRef.current.src = ''
      audioRef.current = null
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current)
      audioUrlRef.current = null
    }
  }, [])

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      releaseActiveAudio()
    }
  }, [releaseActiveAudio])

  const speak = useCallback(async (textToSpeak: string, options: SpeakOptions = {}) => {
    if (!textToSpeak || isLoadingRef.current) return

    const summary = extractSummary(textToSpeak, maxLength)
    if (!summary) return

    if (!options.force && summary === lastSpokenSummaryRef.current) return

    releaseActiveAudio()

    isLoadingRef.current = true
    setIsLoading(true)
    lastSpokenSummaryRef.current = summary

    try {
      const config = getAxiosDefaultConfig()
      const response = await axios.post(
        `${openAIUrl}/voice/speak`,
        { text: summary, voice },
        {
          ...config,
          responseType: 'blob',
          timeout: 60000,
        }
      )

      const audioUrl = URL.createObjectURL(response.data)
      audioUrlRef.current = audioUrl
      const audio = new Audio(audioUrl)
      audioRef.current = audio

      audio.onplay = () => {
        setIsPlaying(true)
        onPlayStartRef.current?.()
      }

      audio.onended = () => {
        setIsPlaying(false)
        onPlayEndRef.current?.()
        if (audioUrlRef.current) {
          URL.revokeObjectURL(audioUrlRef.current)
          audioUrlRef.current = null
        }
      }

      audio.onerror = () => {
        setIsPlaying(false)
        onErrorRef.current?.('Failed to play audio')
        if (audioUrlRef.current) {
          URL.revokeObjectURL(audioUrlRef.current)
          audioUrlRef.current = null
        }
      }

      await audio.play()
    } catch (err: any) {
      console.error('[VoiceOutput] Error:', err)
      if (err?.response?.status === 429) {
        onErrorRef.current?.('Too many speech requests. Please wait a moment and try again.')
      } else {
        const errorMessage = err.response?.data?.error || 'Failed to generate speech'
        onErrorRef.current?.(errorMessage)
      }
    } finally {
      isLoadingRef.current = false
      setIsLoading(false)
    }
  }, [maxLength, voice, releaseActiveAudio])

  const stop = useCallback(() => {
    releaseActiveAudio()
    setIsPlaying(false)
    onPlayEndRef.current?.()
  }, [releaseActiveAudio])

  // Auto-play when text changes (deduped by raw text + spoken summary)
  useEffect(() => {
    if (!autoPlay || !text || text === lastAutoTextRef.current) return
    lastAutoTextRef.current = text
    speak(text)
  }, [text, autoPlay, speak])

  const handleClick = useCallback(() => {
    if (isPlaying) {
      stop()
    } else if (text) {
      speak(text, { force: true })
    }
  }, [isPlaying, text, stop, speak])

  return (
    <div className={`tw-relative tw-inline-flex tw-items-center tw-justify-center ${className}`}>
      {/* Animation when playing */}
      {showAnimation && isPlaying && (
        <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-z-10">
          <VoiceAnimation state="speaking" size="sm" />
        </div>
      )}

      {/* Speaker button */}
      <button
        type="button"
        onClick={handleClick}
        disabled={isLoading || !text}
        className={`
          tw-relative tw-z-20 tw-flex tw-items-center tw-justify-center
          tw-w-8 tw-h-8 tw-rounded-full tw-transition-all tw-duration-200
          ${!text
            ? 'tw-text-gray-300 tw-cursor-not-allowed'
            : isPlaying
            ? 'tw-bg-transparent tw-text-white'
            : 'tw-text-gray-500 hover:tw-text-gray-700 hover:tw-bg-gray-100'
          }
        `}
        title={isPlaying ? 'Stop playback' : 'Play response'}
      >
        {isLoading ? (
          <span className="fa fa-spinner fa-spin tw-text-sm" />
        ) : isPlaying ? (
          <span className="fa fa-stop tw-text-sm" />
        ) : (
          <span className="fa fa-volume-up tw-text-sm" />
        )}
      </button>
    </div>
  )
}

export default VoiceOutput
